import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Gift, Loader2, ArrowRight, CheckCircle2, ShieldAlert, ArrowLeft, Sparkles,
} from "lucide-react";

/**
 * Redeem — public page that turns a comp code (e.g. CPG-FULL-K7XR-B2N9)
 * into free access.
 *   1. Fetches /api/access-codes/{code}/preview so anon visitors see the tier
 *      and duration BEFORE being asked to sign in.
 *   2. If the visitor is not signed in, sends them to /login?next=… with the
 *      code preserved so they land back here after auth.
 *   3. On confirm, POSTs /api/access-codes/redeem and lands the user on the
 *      dashboard.
 */
export default function Redeem() {
  const [params] = useSearchParams();
  const code = (params.get("code") || "").trim().toUpperCase();
  const { user, status, refresh } = useAuth();
  const nav = useNavigate();

  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemed, setRedeemed] = useState(null);

  useEffect(() => {
    if (!code) {
      setPreviewError("This link is missing a code. Ask the person who sent it to double-check.");
      setLoading(false);
      return;
    }
    let alive = true;
    api.get(`/access-codes/${code}/preview`)
      .then((r) => { if (alive) setPreview(r.data); })
      .catch((err) => { if (alive) setPreviewError(formatError(err) || "This code isn't valid."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [code]);

  const doRedeem = async () => {
    setRedeeming(true);
    try {
      const { data } = await api.post("/access-codes/redeem", { code });
      setRedeemed(data);
      // Refresh auth so header/sidebar pick up the new tier
      try { await refresh(); } catch {}
      toast.success(`You're on the ${data.tier_label} plan`);
    } catch (err) {
      toast.error(formatError(err));
    } finally { setRedeeming(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-foreground" data-testid="redeem-page">
      <header className="border-b border-shell-border/70 bg-white/80 backdrop-blur">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-6 md:px-10 py-4">
          <Link to="/" className="font-display text-lg tracking-tight hover:text-primary transition">CPG Institute</Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground transition">
            <ArrowLeft className="w-3.5 h-3.5" /> Back home
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 md:px-10 py-16 md:py-20">
        {loading && (
          <div className="inline-flex items-center gap-2 text-sm text-muted-foreground" data-testid="redeem-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking your invite…
          </div>
        )}

        {!loading && previewError && (
          <div data-testid="redeem-invalid">
            <p className="text-xs uppercase tracking-[0.28em] text-primary font-bold">Invitation</p>
            <h1 className="font-display text-4xl md:text-5xl mt-3 tracking-tight">
              This invite can't be used
            </h1>
            <p className="mt-4 text-muted-foreground leading-relaxed max-w-lg">{previewError}</p>
            <div className="mt-6 rounded-2xl border border-shell-border bg-white p-5 flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-pink-500 shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold">Common reasons</p>
                <ul className="mt-1 list-disc list-inside text-muted-foreground space-y-0.5 text-[13px]">
                  <li>The code was already redeemed by someone else.</li>
                  <li>The admin has revoked or expired the code.</li>
                  <li>There's a typo in the URL — the code is case-insensitive.</li>
                </ul>
              </div>
            </div>
            <Link to="/account/subscription" className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-shell-side text-white px-5 py-2.5 text-sm font-semibold hover:bg-shell-side/90 transition" data-testid="redeem-see-pricing">
              See regular pricing <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {!loading && preview && !redeemed && (
          <div data-testid="redeem-preview">
            <p className="text-xs uppercase tracking-[0.28em] text-primary font-bold inline-flex items-center gap-1.5">
              <Gift className="w-3 h-3" /> Your invite
            </p>
            <h1 className="font-display text-4xl md:text-5xl mt-3 tracking-tight leading-[1.05]">
              You've been given <span className="text-grad" data-testid="redeem-tier-label">{preview.tier_label}</span> access.
            </h1>
            <p className="mt-5 text-muted-foreground leading-relaxed max-w-lg">
              {preview.duration_days ? (
                <>Redeem this invite to unlock <strong>{preview.tier_label}</strong> for <strong data-testid="redeem-duration">{preview.duration_days} days</strong>. No card, no strings — the counter starts when you click the button below.</>
              ) : (
                <>Redeem this invite to unlock <strong>{preview.tier_label}</strong> with <strong data-testid="redeem-duration">lifetime access</strong>. No card, no strings.</>
              )}
            </p>

            {preview.note && (
              <p className="mt-4 rounded-xl bg-white border border-shell-border px-4 py-3 text-sm italic text-muted-foreground" data-testid="redeem-note">
                “{preview.note}”
              </p>
            )}

            <div className="mt-8 rounded-2xl border border-shell-border bg-white p-5">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Invite code</p>
              <p className="mt-1 font-mono text-lg tracking-widest" data-testid="redeem-code">{preview.code}</p>
            </div>

            <div className="mt-8">
              {status === "checking" && (
                <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading account…
                </div>
              )}
              {status === "authenticated" && (
                <button
                  type="button"
                  onClick={doRedeem}
                  disabled={redeeming}
                  data-testid="redeem-confirm-btn"
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary text-white px-6 py-3 text-sm font-bold hover:bg-primary/90 disabled:opacity-60 transition"
                >
                  {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {redeeming ? "Unlocking…" : "Unlock my access"}
                </button>
              )}
              {status !== "checking" && status !== "authenticated" && (
                <div className="flex items-center gap-3 flex-wrap">
                  <Link
                    to={`/login?next=${encodeURIComponent(`/redeem?code=${code}`)}`}
                    data-testid="redeem-signin-link"
                    className="inline-flex items-center gap-1.5 rounded-full bg-primary text-white px-5 py-3 text-sm font-bold hover:bg-primary/90 transition"
                  >
                    Sign in to redeem <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to={`/signup?next=${encodeURIComponent(`/redeem?code=${code}`)}&ref=${encodeURIComponent(code)}`}
                    data-testid="redeem-signup-link"
                    className="inline-flex items-center gap-1.5 rounded-full border border-shell-border bg-white px-5 py-3 text-sm font-semibold hover:border-primary/40 transition"
                  >
                    New here? Create an account
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && redeemed && (
          <div data-testid="redeem-success">
            <p className="text-xs uppercase tracking-[0.28em] text-emerald-600 font-bold inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3" /> Unlocked
            </p>
            <h1 className="font-display text-4xl md:text-5xl mt-3 tracking-tight leading-[1.05]">
              You're on the <span className="text-grad">{redeemed.tier_label}</span> plan.
            </h1>
            <p className="mt-5 text-muted-foreground leading-relaxed max-w-lg">
              {redeemed.comp_expires_at ? (
                <>Your access is live until <strong>{new Date(redeemed.comp_expires_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</strong>. Warren's already loaded your dashboard.</>
              ) : (
                <>Your access is unlimited — welcome in. Warren's already loaded your dashboard.</>
              )}
            </p>
            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => nav("/dashboard")}
                data-testid="redeem-go-dashboard"
                className="inline-flex items-center gap-1.5 rounded-full bg-shell-side text-white px-6 py-3 text-sm font-bold hover:bg-shell-side/90 transition"
              >
                Go to my dashboard <ArrowRight className="w-4 h-4" />
              </button>
              <Link
                to="/welcome"
                className="inline-flex items-center gap-1.5 rounded-full border border-shell-border bg-white px-5 py-3 text-sm font-semibold hover:border-primary/40 transition"
              >
                Take the tour first
              </Link>
            </div>
            {user?.email && (
              <p className="mt-6 text-xs text-muted-foreground">
                Applied to <strong>{user.email}</strong>. You can see this in Account → Subscription & Billing.
              </p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
